import styled, { css } from 'styled-components'
// components
import { CallChatDisplay, CallTypeLabel, ChatActions, ChatHeader } from 'features/calls/components'
// types
import { CallTypeToJobTypeEnum, ICall } from 'features/calls/types'
import { IClaim } from 'features/claims/types'
// hooks
import { useAppDispatch, useAppSelector } from 'appState/store'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { useCallTranscript } from 'features/calls/callsHooks'
import { callsSliceLiveCallInitialState, updateLiveCall } from 'features/calls/callsSlice'
import { softphone } from 'features/calls/utils/softphone'
import { isCurrentUserClaimOwner } from 'features/claims/utils'
import { getClaimCallSummaryPath } from 'features/claims/utils/claimsPathUtils'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatPhoneNumber } from 'utils'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'
import { notificator } from 'utils/notificator/notificator'
import { BrowserMutedNotification } from './BrowserMutedNotification'

interface IProps {
  call: ICall
  claim: IClaim
  styles?: any
}

const CallChat = ({ call, claim, styles }: IProps) => {
  // global state
  const { transcript, isConnected, isMutedAudio, startTime, activeCallJobId } = useAppSelector(
    (state) => state.calls.talkToPayorRepLiveCall,
  )
  const navigate = useNavigate()
  const { user } = useAppSelector((state) => state.auth)

  const [isCallEnded, setCallEnd] = useState(false)

  // utils
  const dispatch = useAppDispatch()
  const rendered = useRef(false)

  // api
  const { data: callWithTranscript } = useCallTranscript(call.jobId)

  useEffect(() => {
    !!callWithTranscript?.data?.length && softphone.updateCallTranscript(callWithTranscript.data)
  }, [callWithTranscript])

  useEffect(() => {
    if (rendered.current) return
    if (isConnected) {
      if (softphone.audioCtx?.state === 'suspended') {
        if (!isMutedAudio) dispatch(updateLiveCall({ isMutedAudio: true }))
        notificator.notify({
          heading: <BrowserMutedNotification />,
          type: 'warn',
        })
        //notificator.warn(<BrowserMutedNotification />)
      } else {
        if (isMutedAudio) softphone.unmuteAudio()
      }
    }
    rendered.current = true
  }, [isConnected])

  // handlers
  const handleToggleMute = () => {
    isMutedAudio ? softphone.unmuteAudio() : softphone.muteAudio()
  }

  const redirectToClaimCallSummary = (call: ICall) => {
    const tenantId = getTntIdForNavigation()
    navigate(getClaimCallSummaryPath(tenantId, claim!.oaiClaimId, call.jobId))
  }

  const handleEndCall = () => {
    setCallEnd(true)
    softphone.hangUp()
    dispatch(updateLiveCall({ ...callsSliceLiveCallInitialState, lastCallJobIdUserCompleted: call.jobId }))
    if (!isConnected) {
      setCallEnd(false)
      redirectToClaimCallSummary(call)
    }
  }

  // variables
  const isOwner = isCurrentUserClaimOwner(claim?.ownerId, user!)
  const isManualCall = call.jobType === CallTypeToJobTypeEnum.standard
  const payerDisplayName = isManualCall ? formatPhoneNumber(call.phoneNumber) : claim.insurerName
  const callTitle = isManualCall
    ? `Call to ${formatPhoneNumber(call.phoneNumber)} in progress`
    : undefined
  const callStartTime = startTime || call.jobStarted || call.jobQueued || call.jobRequested
  const showLabel = activeCallJobId && call.jobType !== CallTypeToJobTypeEnum.diagnosticHeadset

  return (
    <>
      {isCallEnded ? (
        <LoadingContainer>
          <div className="loader-content">
            <span>Call Completed</span>
            <small>Preparing call summary</small>
            <LoadingAnimation />
          </div>
        </LoadingContainer>
      ) : (
        ''
      )}

      <Section styles={styles}>
        {showLabel && (
          <section className="call-type-label">
            <CallTypeLabel claim={claim} title={callTitle} />
          </section>
        )}
        <section className="header">
          <ChatHeader
            isMuted={isMutedAudio}
            providerName={payerDisplayName}
            providerPhoneNumber={formatPhoneNumber(call.phoneNumber)}
            onIconClick={handleToggleMute}
          />
        </section>
        <section className="body">
          <section className="messages">
            <CallChatDisplay call={call} transcript={transcript} claim={claim} callStartTime={callStartTime} />
          </section>
          <section className="actions">
            <ChatActions
              onCloseCall={handleEndCall}
              onToggleMute={softphone.toggleMute}
              onTakeOver={softphone.takeOver}
              isOwner={isOwner}
              call={call}
            />
          </section>
        </section>
      </Section>
    </>
  )
}

const LoadingContainer = styled.div`
  background-color: rgb(121 121 121 / 85%);
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: calc(calc(calc(100vh - 0rem) - 0rem) - 0rem);
  z-index: 9999;
  .loader-content {
    display: flex;
    flex-direction: column;
    height: 70%;
    align-items: center;
    justify-content: center;
    color: #fff;
    span {
      font-size: 1.5rem;
      font-weight: 700;
    }
    small {
      margin-bottom: 0.625rem;
      font-size: 0.875rem;
    }
  }
`

const Section = styled.section<{ styles: any }>`
  position: relative;
  height: ${({ theme }) => theme.util.subtractFromAbsoluteViewportHeight(theme.constants.navbarHeight, 2)};

  .call-type-label {
    position: absolute;
    inset: calc(0.625rem - ${({ theme }) => theme.constants.navbarHeight}) 0 auto 0;
  }

  .header {
    height: ${({ theme }) => theme.constants.navbarHeight};
    width: 100%;
    display: flex;
    justify-content: start;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
  }

  .body {
    position: relative;
    width: 100%;
    height: ${({ theme }) => theme.util.subtractFromAbsoluteViewportHeight(theme.constants.navbarHeight, 3)};
    .messages {
      display: flex;
      flex-direction: column;
      height: ${({ theme }) => theme.util.subtractFromAbsoluteViewportHeight(theme.constants.navbarHeight, 5)};
      align-items: flex-start;

      #chat-display-transcript {
        padding-bottom: 3rem;
      }
    }
    .actions {
      padding: 1.5rem 2rem 2.5rem;
    }
  }
  ${({ styles }) =>
    styles
      ? css`
          ${styles}
        `
      : null}
`

export { CallChat }
